import {StoreServiceGenInfoFormFieldsType} from "../../../components/offering-form/services/gen-info/types";
import {StoreServiceSettingsFormFieldsType} from "../../../components/offering-form/services/settings/types";
import {SubscriptionTiersFormValues} from "../../../components/offering-form/subscriptions/subscription-tier-form/types";
import {OfferingFormCheckoutFormValues} from "../../../components/offering-form/general/checkout-form/types";
import {offeringCheckoutFormValidationSchema} from "../../../components/offering-form/general/checkout-form/validation-schema";
import yup from "../../../utils/yup-extended";
import {tierPricingValidationSchemaFields} from "../tier-pricing-schema";

const createSubscriptionFormValidationSchemaBase = yup.object().shape({
  name: yup.string().trim().required("Subscription name is required"),
  description: yup
    .string()
    .transform((value) => (typeof value === "string" ? value : ""))
    .trim()
    .required("Description is required")
    .default(""),
  hideFromProfile: yup.bool().default(false),
  requestCustomerPhoneNumber: yup.bool().default(false),
  isUrlRedirect: yup.bool().default(false),
  limitUnitsPerTransaction: yup.bool().default(false).optional(),
  url_redirect: yup
    .string()
    .trim()
    .nullable()
    .when("isUrlRedirect", {
      is: (isUrlRedirect: boolean) => isUrlRedirect,
      then: (schema) => schema.required("Redirect URL is required").urlWithEmoji().trim(),
      otherwise: (schema) => schema.notRequired(),
    }),
  enable_currency_conversion: yup.bool().default(true).optional(),
  selected_currencies: yup.array().of(yup.string()).default([]).optional(),
  tiers: yup.array().of(
    yup.object().shape({
      tierName: yup
        .string()
        .trim()
        .required("Enter a name for this tier so you have at least one complete tier"),
      ...tierPricingValidationSchemaFields,
      billingFrequency: yup.string().when("priceType", {
        is: "free",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.oneOf(["daily", "weekly", "monthly", "yearly"]).required(),
      }),
      billingCount: yup.string().when("priceType", {
        is: "free",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.oneOf(["custom", "until_cancel"]).required(),
      }),
      billingCycles: yup
        .number()
        .transform((value, originalValue) => (originalValue === "" ? undefined : value))
        .when(["priceType", "billingCount"], {
          is: (priceType: string, billingCount: string) =>
            priceType !== "free" && billingCount === "custom",
          then: (schema) =>
            schema
              .typeError("Please enter the number of billing cycles")
              .integer("Billing cycles must be a whole number")
              .min(1, "Billing cycles must be at least 1")
              .required("Please enter the number of billing cycles"),
          otherwise: (schema) => schema.notRequired(),
        }),
      featuresEnabled: yup.bool().default(false),
      features: yup.array().when("featuresEnabled", {
        is: (featuresEnabled?: boolean) => featuresEnabled === true,
        then: (schema) =>
          schema.test(
            "at-least-one-feature",
            "Please enter at least one feature",
            (rows) => {
              const list = rows ?? [];
              return list.some(
                (r: {value?: string}) =>
                  typeof r?.value === "string" && r.value.trim().length > 0
              );
            }
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      newFeatureInput: yup.string().when("featuresEnabled", {
        is: (featuresEnabled?: boolean) => featuresEnabled === true,
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.notRequired(),
      }),
      redirectAfterPurchaseEnabled: yup.bool().default(false),
      redirectAfterPurchase: yup
        .mixed<"none" | "telegram" | "custom-url">()
        .oneOf(["none", "telegram", "custom-url"])
        .when("redirectAfterPurchaseEnabled", {
          is: true,
          then: (schema) => schema.required("Redirect destination is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      telegramGroupId: yup
        .string()
        .when(["redirectAfterPurchaseEnabled", "redirectAfterPurchase"], {
          is: (enabled: boolean, destination: string) =>
            enabled && destination === "telegram",
          then: (schema) =>
            schema
              .required("Telegram group is required")
              .test(
                "telegram-group",
                "Telegram group is required",
                (value) => value !== "none"
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      // A custom redirect is validated independently for every subscription tier.
      urlRedirect: yup
        .string()
        .nullable()
        .when(["redirectAfterPurchaseEnabled", "redirectAfterPurchase"], {
          is: (enabled: boolean, destination: string) =>
            enabled && destination === "custom-url",
          then: (schema) =>
            schema.required("Redirect URL is required").urlWithEmoji().trim(),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
  ),
});

export const createSubscriptionFormValidationSchema =
  createSubscriptionFormValidationSchemaBase.concat(offeringCheckoutFormValidationSchema);

export const createSubscriptionBasicFormValidationSchema =
  createSubscriptionFormValidationSchemaBase;

export const subscriptionCheckoutFormValidationSchema =
  offeringCheckoutFormValidationSchema;

export type CreateSubscriptionFormValues = StoreServiceGenInfoFormFieldsType &
  StoreServiceSettingsFormFieldsType &
  SubscriptionTiersFormValues &
  Partial<Pick<OfferingFormCheckoutFormValues, "bookingQuestions">> & {
    enable_currency_conversion?: boolean;
    selected_currencies?: string[];
  };
